/**
 * @file ChangeItemsList.tsx
 * @description Список изменений части или версии вида сайта (phase19.16 Р1;
 * uiux-design-r1 §6): «Кнопки: [■] #FFA915 → [■] #4E5B3A», «Шрифт заголовков:
 * Montserrat → Lora», «В шапку добавлен пункт «Подарки»». Цвета показываются
 * образцом рядом с кодом — не только текстом.
 *
 * phase19.28 (QA D-D7): у полей-ссылок на страницу (`…PageId`) вместо
 * сырого id — название страницы «…» (`usePageTitles`).
 */

import { PAGE_ID_PATH, usePageTitles } from "../../hooks/site-view/usePageTitles";
import type { SiteChangeItem } from "../../lib/site-view/types";

const HEX = /^#[0-9a-fA-F]{6}(?:[0-9a-fA-F]{2})?$/;

function Value({ value }: { value: string | null }) {
  if (value === null) return <span>—</span>;
  if (!HEX.test(value)) return <span>{value}</span>;
  return (
    <span className="inline-flex items-center gap-1">
      <span aria-hidden className="inline-block size-3.5 rounded-sm [box-shadow:inset_0_0_0_1px_var(--border)]" style={{ background: value }} />
      <span className="font-mono">{value}</span>
    </span>
  );
}

/** Значение поля-ссылки на страницу: название «…», пока не узнали — «страница». */
function pageValue(value: string | null, titles: ReadonlyMap<string, string>): string | null {
  if (value === null) return null;
  const title = titles.get(value);
  return title ? `«${title}»` : "страница сайта";
}

export function ChangeItemsList({ items, limit }: { items: SiteChangeItem[]; limit?: number }) {
  const shown = limit ? items.slice(0, limit) : items;
  const titles = usePageTitles(shown.some((item) => PAGE_ID_PATH.test(item.path)));
  const show = (item: SiteChangeItem, value: string | null) => (PAGE_ID_PATH.test(item.path) ? pageValue(value, titles) : value);
  return (
    <ul className="flex flex-col gap-1">
      {shown.map((item) => (
        <li key={`${item.path}:${item.label}`} className="type-body flex flex-wrap items-center gap-x-1 text-on-surface">
          {item.before === null && item.after === null ? (
            item.label
          ) : (
            <>
              <span>{item.label}:</span>
              <Value value={show(item, item.before)} />
              <span aria-label="стало">→</span>
              <Value value={show(item, item.after)} />
            </>
          )}
        </li>
      ))}
      {limit && items.length > limit ? (
        <li className="type-caption text-on-surface-variant">и ещё {items.length - limit}</li>
      ) : null}
    </ul>
  );
}
